/**
 * useAuth Hook
 * 
 * Custom hook that handles authentication business logic
 * Separates business logic from state management
 */

import { useCallback } from 'react'
import { useAuth as useAuthContext } from '../contexts/AuthContext'
import { authApi } from '../features/auth/authApi'
import { LoginCredentials, RegisterCredentials } from '../types'
import { handleApiError, logError } from '../utils/errorHandler'

export const useAuth = () => {
  const { state, dispatch } = useAuthContext()

  const login = useCallback(async (credentials: LoginCredentials) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    
    try {
      const result = await authApi.login(credentials)
      dispatch({ type: 'SET_USER', payload: result })
    } catch (error) {
      const appError = handleApiError(error)
      logError(appError, 'useAuth.login')
      dispatch({ 
        type: 'SET_ERROR', 
        payload: appError.message
      })
    }
  }, [dispatch])

  const register = useCallback(async (credentials: RegisterCredentials) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    
    try {
      const result = await authApi.register(credentials)
      dispatch({ type: 'SET_USER', payload: result })
    } catch (error) {
      const appError = handleApiError(error)
      logError(appError, 'useAuth.register')
      dispatch({ 
        type: 'SET_ERROR', 
        payload: appError.message
      })
    }
  }, [dispatch])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch (error) {
      const appError = handleApiError(error)
      logError(appError, 'useAuth.logout')
      // Continue with logout even if API call fails
    } finally {
      dispatch({ type: 'LOGOUT' })
    }
  }, [dispatch])

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' })
  }, [dispatch])

  return {
    // State
    user: state.user,
    token: state.token,
    isLoading: state.isLoading,
    error: state.error,
    isAuthenticated: !!state.user,
    
    // Actions
    login,
    register,
    logout,
    clearError,
  }
} 